const WIKI_SUMMARY = 'https://en.wikipedia.org/api/rest_v1/page/summary/';
const CACHE_KEY = 'kwibuka.memorial.wikipedia.cache.v2';

export const HISTORICAL_TIMELINE = [
  {
    id: 'rwandan-revolution',
    year: 1959,
    date: '1959',
    title: 'Anti-Tutsi violence and exile begins',
    room: 'origins',
    wiki: 'Rwandan_Revolution',
    body: 'Violence and political upheaval in 1959 forced many Tutsi families into exile and began a long period of persecution, displacement, and state-backed division.',
  },
  {
    id: 'first-republic',
    year: 1962,
    date: '1962',
    title: 'Independence and institutionalized exclusion',
    room: 'origins',
    wiki: 'History_of_Rwanda',
    body: 'After independence, identity-based exclusion hardened inside public life, education, and political administration.',
  },
  {
    id: 'bugesera-violence',
    year: 1963,
    date: '1963-1964',
    title: 'Mass killings and refugee crisis',
    room: 'origins',
    wiki: 'Rwandan_Revolution',
    body: 'Attacks on Tutsi civilians intensified, creating large refugee communities in neighboring countries and deepening the politics of exclusion.',
  },
  {
    id: 'second-republic',
    year: 1973,
    date: '1973',
    title: 'Habyarimana takes power',
    room: 'origins',
    wiki: 'Juvenal_Habyarimana',
    body: 'A military coup brought Juvenal Habyarimana to power. Quotas and identity politics remained embedded in state systems.',
  },
  {
    id: 'rpf-formed',
    year: 1987,
    date: '1987',
    title: 'Rwandan Patriotic Front formed',
    room: 'preparation',
    wiki: 'Rwandan_Patriotic_Front',
    body: 'Rwandan exiles organized politically and militarily around return, rights, and ending statelessness.',
  },
  {
    id: 'civil-war',
    year: 1990,
    date: '1990',
    title: 'Civil war and renewed anti-Tutsi arrests',
    room: 'preparation',
    wiki: 'Rwandan_Civil_War',
    body: 'The RPF invasion began the civil war. Inside Rwanda, Tutsi civilians were frequently treated as accomplices and targeted by arrests and propaganda.',
  },
  {
    id: 'bugesera-1992',
    year: 1992,
    date: '1992',
    title: 'Bugesera attacks and rehearsal violence',
    room: 'preparation',
    wiki: 'Rwandan_genocide',
    body: 'Mass violence in places such as Bugesera foreshadowed methods later used during the genocide.',
  },
  {
    id: 'rtlm',
    year: 1993,
    date: '1993',
    title: 'Extremist media escalates hate propaganda',
    room: 'preparation',
    wiki: 'Radio_Television_Libre_des_Mille_Collines',
    body: 'RTLM and extremist publications normalized dehumanization and helped coordinate fear, accusation, and later killing.',
  },
  {
    id: 'arusha',
    year: 1993,
    date: '1993-08',
    title: 'Arusha Accords signed',
    room: 'preparation',
    wiki: 'Arusha_Accords_(Rwanda)',
    body: 'The peace agreement created a political opening while extremists prepared to sabotage power sharing.',
  },
  {
    id: 'plane',
    year: 1994,
    date: '1994-04-06',
    title: 'Presidential plane shot down',
    room: 'hundredDays',
    wiki: 'Assassination_of_Juvenal_Habyarimana_and_Cyprien_Ntaryamira',
    body: 'The assassination of President Habyarimana was immediately used as the trigger for organized mass killing.',
  },
  {
    id: 'genocide',
    year: 1994,
    date: '1994-04-07',
    title: 'Genocide against the Tutsi begins',
    room: 'hundredDays',
    wiki: 'Rwandan_genocide',
    body: 'Over roughly 100 days, Tutsi civilians and moderate Hutu were targeted through organized killing across Rwanda.',
  },
  {
    id: 'liberation',
    year: 1994,
    date: '1994-07',
    title: 'RPF captures Kigali and ends organized killing',
    room: 'hundredDays',
    wiki: 'Rwandan_genocide',
    body: 'RPF military advances ended organized killing in Kigali and then across the country as the genocidal government fled.',
  },
];

const WIKI_TOPICS = [
  'Rwandan_genocide',
  'Kigali_Genocide_Memorial',
  'Rwandan_Revolution',
  'Rwandan_Civil_War',
  'Radio_Television_Libre_des_Mille_Collines',
  'Arusha_Accords_(Rwanda)',
  'Rwandan_Patriotic_Front',
  'Juvenal_Habyarimana',
  'Assassination_of_Juvenal_Habyarimana_and_Cyprien_Ntaryamira',
];

const FALLBACK_EXTRACTS = {
  Rwandan_genocide: 'The 1994 Genocide against the Tutsi in Rwanda took place over roughly 100 days and killed hundreds of thousands of people.',
  Kigali_Genocide_Memorial: 'The Kigali Genocide Memorial is a place of remembrance and education in Kigali, Rwanda.',
  Rwandan_Revolution: 'The Rwandan Revolution was a period of political upheaval and violence from 1959 into the early 1960s.',
  Rwandan_Civil_War: 'The Rwandan Civil War began in 1990 and shaped the political crisis that preceded the 1994 genocide.',
  Radio_Television_Libre_des_Mille_Collines: 'RTLM was an extremist Rwandan radio station that broadcast hate propaganda before and during the genocide.',
  'Arusha_Accords_(Rwanda)': 'The Arusha Accords were peace agreements intended to end the Rwandan Civil War and create a power-sharing government.',
  Rwandan_Patriotic_Front: 'The Rwandan Patriotic Front was formed by Rwandan exiles and later ended the genocide militarily.',
  Juvenal_Habyarimana: 'Juvenal Habyarimana was president of Rwanda from 1973 until his assassination on April 6, 1994.',
  Assassination_of_Juvenal_Habyarimana_and_Cyprien_Ntaryamira: 'The aircraft carrying Presidents Juvenal Habyarimana and Cyprien Ntaryamira was shot down on April 6, 1994.',
};

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function writeCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (_) {}
}

async function fetchSummary(title) {
  const cache = readCache();
  if (cache[title]) return cache[title];

  let record;
  try {
    const response = await fetch(`${WIKI_SUMMARY}${encodeURIComponent(title)}`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Wikipedia ${response.status}`);
    const data = await response.json();
    record = {
      title: data.title || title.replaceAll('_', ' '),
      extract: data.extract || FALLBACK_EXTRACTS[title] || '',
      image: data.thumbnail?.source || data.originalimage?.source || '',
      url: data.content_urls?.desktop?.page || `https://en.wikipedia.org/wiki/${title}`,
      fetchedAt: new Date().toISOString(),
      source: 'Wikipedia REST API',
    };
  } catch (error) {
    record = {
      title: title.replaceAll('_', ' '),
      extract: FALLBACK_EXTRACTS[title] || 'Wikipedia summary unavailable in this session.',
      image: '',
      url: `https://en.wikipedia.org/wiki/${title}`,
      fetchedAt: new Date().toISOString(),
      source: 'Wikipedia fallback cache',
      error: error.message,
    };
  }

  cache[title] = record;
  writeCache(cache);
  persistMuseumRecord(title, record);
  return record;
}

async function persistMuseumRecord(title, record) {
  const payload = {
    key: title,
    source: record.source,
    payload: record,
    fetched_at: record.fetchedAt,
  };

  try {
    const response = await fetch('/api/wiki-cache', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (response.ok) return;
  } catch (_) {}

  const cache = readCache();
  cache[`db:${title}`] = payload;
  writeCache(cache);
}

export async function loadMuseumCollection() {
  const summaries = await Promise.all(WIKI_TOPICS.map(async title => [title, await fetchSummary(title)]));
  const byTitle = Object.fromEntries(summaries);
  const timeline = HISTORICAL_TIMELINE.map(item => ({
    ...item,
    summary: byTitle[item.wiki],
  }));

  return {
    topics: summaries.map(([, summary]) => summary),
    timeline,
  };
}
